import { Box } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions/FormModalActions';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import { AugmentedUpdateHouseholdInput } from '@/types/schema';
import * as diagnostics from '@/utils/diagnostics';

import { ClientReassignmentForm } from './ClientReassignmentForm';
import {
  ClientReassignmentFormShape,
  ClientReassignmentModalCloseReason,
} from './ClientReassignmentModal.types';
import { useBulkReassignHouseholdsMutation } from './graphql/ClientReassignmentModal.generated';

export interface ClientReassignmentModalProps {
  isOpen: boolean;
  assignedHouseholds: Array<{ id: string; displayName: string }>;
  excludeUserId?: string;
  onClose: (
    reason: ClientReassignmentModalCloseReason,
    reassignToUserId: null | string
  ) => void;
}

export function ClientReassignmentModal({
  isOpen,
  assignedHouseholds,
  excludeUserId,
  onClose,
}: ClientReassignmentModalProps) {
  const { formRef, handleSave } = useFormSaveHandler();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const formMethods = useForm<ClientReassignmentFormShape>({
    defaultValues: {
      reassignedTo: '',
    },
  });
  const { handleSubmit, formState, shouldBlockNavigation } = formMethods;

  const [bulkReassignHouseholds, { loading: isReassigning }] =
    useBulkReassignHouseholdsMutation({
      onError: (error) => {
        showFeedback(
          "We weren't able to reassign the clients. Please try again."
        );
        reportError('Could not reassign households', error);
      },
      onCompleted: () => {
        showFeedback('Clients successfully reassigned.', {
          variant: 'success',
        });
      },
    });

  const onSubmit = handleSubmit(async (formData) => {
    try {
      const inputs: AugmentedUpdateHouseholdInput[] = assignedHouseholds.map(
        (household) => ({
          id: household.id,
          update: {
            primaryRelationshipOwnerID: formData.reassignedTo,
          },
        })
      );

      await bulkReassignHouseholds({
        variables: {
          inputs,
        },
      });

      onClose(
        ClientReassignmentModalCloseReason.COMPLETE,
        formData.reassignedTo
      );
    } catch (err) {
      diagnostics.error('Could not reassign households', err as Error, {
        householdIds: assignedHouseholds.map((h) => h.id).join(', '),
        reassignToUserId: formData.reassignedTo,
      });
    }
  });

  function handleCancel() {
    onClose(ClientReassignmentModalCloseReason.CLOSE, null);
  }

  const disabled = formState?.isSubmitting || isReassigning;

  return (
    <FormProvider {...formMethods}>
      <FormModal
        isOpen={isOpen}
        heading="Reassign clients to a new relationship manager"
        rightHeaderContent={<Box />}
        onClose={handleCancel}
        shouldBlockClose={shouldBlockNavigation}
        actions={
          <FormModalActions.Provider formState={formState} disabled={disabled}>
            <FormModalActions.CancelButton onClick={handleCancel} />
            <Button
              size="sm"
              variant="destructive-primary"
              onClick={handleSave}
            >
              Complete reassignment
            </Button>
          </FormModalActions.Provider>
        }
      >
        <form ref={formRef} onSubmit={onSubmit}>
          <FormFieldsDisabledProvider isDisabled={disabled}>
            <ClientReassignmentForm excludeUserId={excludeUserId} />
          </FormFieldsDisabledProvider>
        </form>
      </FormModal>
    </FormProvider>
  );
}
